import { NavLink, useNavigate } from "react-router";
import {
  BookOpen,
  ClipboardList,
  Users,
  LogOut,
  Settings,
  ShieldCheck,
  Eye,
  EyeOff,
  Grid3X3,
  BarChart3,
  Building2,
  FlaskConical,
  ToggleRight,
  SlidersHorizontal,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { usePrivacy } from "@/lib/privacy";

type Role = "admin" | "bht" | "crm" | "superuser";

type NavItem = {
  to: string;
  label: string;
  icon: typeof BookOpen;
  requiredRole?: Role | Role[];
};

const navSections: { label: string; items: NavItem[] }[] = [
  {
    label: "Klinisk",
    items: [
      { to: "/catalog", label: "Katalog", icon: BookOpen },
      { to: "/anamnesis", label: "Kundebehandling", icon: ClipboardList },
    ],
  },
  {
    label: "Virksomhet",
    items: [
      { to: "/crm", label: "Auction House", icon: Users, requiredRole: "crm" },
      { to: "/companies", label: "Bedrifter", icon: Building2, requiredRole: ["admin", "bht"] },
      { to: "/dashboards", label: "Dashboards", icon: BarChart3, requiredRole: ["admin", "bht"] },
    ],
  },
  {
    label: "Konfigurasjon",
    items: [
      { to: "/helfo-engine-test", label: "HELFO-motor", icon: FlaskConical, requiredRole: "admin" },
      { to: "/correlation-configs", label: "Korrelasjoner", icon: Grid3X3, requiredRole: "admin" },
      { to: "/feature-flags", label: "Feature flags", icon: ToggleRight, requiredRole: "admin" },
      { to: "/authorization", label: "Autorisering", icon: ShieldCheck, requiredRole: "superuser" },
    ],
  },
  {
    label: "Drift",
    items: [
      { to: "/monitoring", label: "Overvåking", icon: Activity, requiredRole: "superuser" },
      { to: "/system", label: "System", icon: Settings, requiredRole: "superuser" },
    ],
  },
];

export function Sidebar() {
  const { logout, impersonatingUserId, stopImpersonating, hasRole } = useAuth();
  const { revealed, toggle: togglePrivacy } = usePrivacy();
  const navigate = useNavigate();

  // Leave the patient page as well. The impersonation token now lives only as
  // long as that view, so stopping without navigating would let the page mint
  // (and log) the same impersonation straight back.
  const handleStopImpersonating = () => {
    stopImpersonating();
    navigate("/anamnesis");
  };

  return (
    <aside className="w-60 shrink-0 border-r border-admin-card-border bg-admin-surface flex flex-col">
      <div className="p-4 border-b border-admin-card-border">
        <h1 className="text-lg font-bold text-admin-primary">Onyxia's Lair</h1>
      </div>
      <nav className="flex-1 p-2 space-y-4 overflow-y-auto">
        {navSections.map((section) => {
          const visibleItems = section.items.filter(
            (item) =>
              !item.requiredRole ||
              [item.requiredRole].flat().some(hasRole) ||
              hasRole("superuser"),
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label} className="space-y-0.5">
              <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-admin-text-subtle">
                {section.label}
              </p>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-admin-primary-active text-admin-primary"
                        : "text-admin-text-secondary hover:bg-admin-slate-hover hover:text-admin-text-primary"
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {impersonatingUserId && (
        <div className="mx-2 mb-2 rounded-md bg-admin-warn-500/10 border border-admin-warn-500/20 p-2">
          <p className="text-xs text-admin-warn-400 font-medium">Impersonerer</p>
          <p className="text-xs text-admin-warn-400/70 truncate">{impersonatingUserId}</p>
          <button
            onClick={handleStopImpersonating}
            className="text-xs text-admin-warn-400 underline mt-1 cursor-pointer hover:text-admin-warn-300"
          >
            Stopp
          </button>
        </div>
      )}

      <div className="p-2 border-t border-admin-card-border space-y-0.5">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-admin-primary-active text-admin-primary"
                : "text-admin-text-subtle hover:bg-admin-slate-hover hover:text-admin-text-primary"
            )
          }
        >
          <SlidersHorizontal className="h-4 w-4" />
          Innstillinger
        </NavLink>
        <button
          onClick={togglePrivacy}
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors w-full cursor-pointer",
            revealed
              ? "bg-admin-primary/15 text-admin-primary hover:bg-admin-primary/25"
              : "bg-admin-warn-500/10 text-admin-warn-400 hover:bg-admin-warn-500/20"
          )}
        >
          {revealed ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {revealed ? "PII synlig" : "PII skjult"}
        </button>
        <button
          onClick={logout}
          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-admin-text-subtle hover:bg-admin-slate-hover hover:text-admin-text-primary transition-colors w-full cursor-pointer"
        >
          <LogOut className="h-4 w-4" />
          Logg ut
        </button>
      </div>
    </aside>
  );
}
